import {
   MAX_ALBUM_PHOTO_BYTES,
   MAX_ALBUM_UPLOAD_FILES,
   isAlbumUploadAllowedMime,
   resolveAlbumUploadMime,
} from './albumUploadConstants';

export type PartitionAlbumUploadFilesResult = {
   withinLimit: File[];
   overLimit: { file: File; message: string }[];
};

export function partitionAlbumUploadFiles(files: File[]): PartitionAlbumUploadFilesResult {
   const withinLimit: File[] = [];
   const overLimit: { file: File; message: string }[] = [];
   const maxMb = (MAX_ALBUM_PHOTO_BYTES / (1024 * 1024)).toFixed(0);
   for (const file of files) {
      if (file.size > MAX_ALBUM_PHOTO_BYTES) {
         overLimit.push({
            file,
            message: `"${file.name}" exceeds ${maxMb}MB limit.`,
         });
      } else {
         withinLimit.push(file);
      }
   }
   return { withinLimit, overLimit };
}

export type UploadAlbumPhotosApiOptions = {
   tripId?: string;
   eventId?: string;
};

export type UploadAlbumPhotoError = { fileName: string; message: string };

export type UploadAlbumPhotosApiResult = {
   uploadedPhotos: unknown[];
   uploadErrors: UploadAlbumPhotoError[];
   /** Files not attempted because batch was sliced to MAX_ALBUM_UPLOAD_FILES */
   truncatedCount: number;
};

function parseUploadResponseJson(text: string): { ok: true; data: any } | { ok: false; error: string } {
   try {
      return { ok: true, data: JSON.parse(text) };
   } catch {
      return { ok: false, error: 'Invalid response from server' };
   }
}

type SingleOutcome =
   | { kind: 'ok'; photos: unknown[] }
   | { kind: 'err'; fileName: string; message: string };

/**
 * Uploads each file in its own multipart request (stays under gateway body limits).
 * Uses Promise.allSettled so one failure does not cancel the rest.
 */
export async function uploadAlbumPhotosOnePerRequest(
   albumId: string,
   files: File[],
   getAuthHeaders: () => Promise<Record<string, string>>,
   options?: UploadAlbumPhotosApiOptions
): Promise<UploadAlbumPhotosApiResult> {
   const toUpload = files.slice(0, MAX_ALBUM_UPLOAD_FILES);
   const truncatedCount = Math.max(0, files.length - toUpload.length);

   const baseUrl = process.env.REACT_APP_API_URL || '';
   const authHeaders = await getAuthHeaders();
   const { 'Content-Type': _ct, ...headersWithoutContentType } = authHeaders;

   const uploadOne = async (file: File): Promise<SingleOutcome> => {
      const mime = resolveAlbumUploadMime(file);
      if (!mime || !isAlbumUploadAllowedMime(mime)) {
         return {
            kind: 'err',
            fileName: file.name,
            message: 'Unsupported file type for album upload.',
         };
      }

      const formData = new FormData();
      formData.append('photos', file, file.name);
      if (options?.tripId) formData.append('tripId', options.tripId);
      if (options?.eventId) formData.append('eventId', options.eventId);

      let response: Response;
      try {
         response = await fetch(`${baseUrl}/api/albums/${albumId}/upload-photos`, {
            method: 'POST',
            headers: headersWithoutContentType,
            body: formData,
         });
      } catch (e) {
         const msg = e instanceof Error ? e.message : 'Network error';
         return { kind: 'err', fileName: file.name, message: msg };
      }

      const text = await response.text();
      const parsed = parseUploadResponseJson(text);
      if (!parsed.ok) {
         return { kind: 'err', fileName: file.name, message: parsed.error };
      }
      const data = parsed.data;

      if (!response.ok) {
         return {
            kind: 'err',
            fileName: file.name,
            message: data?.message || `Upload failed (${response.status})`,
         };
      }

      const batch = data?.uploadedPhotos;
      const photos = Array.isArray(batch) ? batch : [];
      return { kind: 'ok', photos };
   };

   const settled = await Promise.allSettled(toUpload.map((f) => uploadOne(f)));

   const uploadedPhotos: unknown[] = [];
   const uploadErrors: UploadAlbumPhotoError[] = [];

   for (let i = 0; i < settled.length; i++) {
      const o = settled[i];
      const file = toUpload[i];
      if (o.status === 'fulfilled') {
         const v = o.value;
         if (v.kind === 'ok') {
            uploadedPhotos.push(...v.photos);
         } else {
            uploadErrors.push({ fileName: v.fileName, message: v.message });
         }
      } else {
         uploadErrors.push({
            fileName: file?.name ?? '?',
            message: o.reason instanceof Error ? o.reason.message : String(o.reason),
         });
      }
   }

   return { uploadedPhotos, uploadErrors, truncatedCount };
}
